import { AddCircle } from '@mui/icons-material';
import { Box, Checkbox, Grid, Typography } from '@mui/material';
import _ from 'lodash';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { usaStateListWithCodes } from '../../../data/fhirHumanNameData';
import { paperWithTopLabelAndBottomValue } from '../uiComponent';
import {
  AutoCompleteWithTopLabel,
  SelectWithTopLabelComponent,
  TextFieldWithTopLabelComponent,
} from '../uiComponent/fhirComponentUi';
import { capitalizeFirstLetter, getZipCodes } from '../../../config/common';
import { useDispatch, useSelector } from 'react-redux';
import {
  resetStateDetectorState,
  setStateDetectorState,
} from '../../../redux/features/stateDetectorSlice';
import { GET_COUNTRIES, GET_STATES } from '../../../config/address-helper';

const addressInitialState = {
  use: '', // home | work | temp | old | billing - purpose of this address
  type: '', // postal | physical | both
  text: '', // Text representation of the address
  line: [], // Street name, number, direction & P.O. Box etc.
  city: '', // Name of city, town etc.
  district: '', // District name (aka county)
  state: '', // Sub-unit of country (abbreviations ok)
  postalCode: '', // Postal code for area
  country: '', // Country (e.g. can be ISO 3166 2 or 3 letter code)
  period: {}, // Time period when address was/is in use
};

const addressInitialStateFromProps = (addressObject, anyCountry) => {
  let zipCodes = getZipCodes(
    !_.isEmpty(addressObject?.official?.state)
      ? addressObject?.official?.state
      : usaStateListWithCodes?.[0]?.code,
    // addressObject?.official?.country ? addressObject?.official?.country : 'US'
    // addressObject?.official?.country?.slice(0, 2)?.toUpperCase()
    addressObject?.official?.country
      ? addressObject?.official?.country?.slice(0, 2)?.toUpperCase()
      : 'US'
    // "IN"
  );
  let zipCodesSecondary = getZipCodes(
    !_.isEmpty(addressObject?.billing?.state)
      ? addressObject?.billing?.state
      : usaStateListWithCodes?.[0]?.code,
    // 'IN'
    // addressObject?.billing?.country ? addressObject?.billing?.country : 'US'
    // addressObject?.billing?.country?.slice(0, 2)?.toUpperCase()
    addressObject?.billing?.country
      ? addressObject?.billing?.country?.slice(0, 2)?.toUpperCase()
      : 'US'
    // "IN"
  );
  return {
    isBillingAndPrimarySame: addressObject?.billing?.city === '',
    officialUse: addressObject?.official?.use, // home | work | temp | old | billing - purpose of this address
    officialType: addressObject?.official?.type, // postal | physical | both
    officialText: addressObject?.official?.text, // Text representation of the address
    officialAddressLine1: addressObject?.official?.line?.[0] ?? '', // Street name, number, direction & P.O. Box etc.
    officialAddressLine2: addressObject?.official?.line?.[1] ?? '',
    officialCity: addressObject?.official?.city, // Name of city, town etc.
    officialDistrict: addressObject?.official?.district, // District name (aka county)
    officialState: !_.isEmpty(addressObject?.official?.state)
      ? addressObject?.official?.state
      : // : usaStateListWithCodes?.[0]?.code, // Sub-unit of country (abbreviations ok)
      anyCountry == 'IN'
      ? 'AN'
      : 'AL', // Sub-unit of country (abbreviations ok)
    officialPostalCode: zipCodes.find(f => {
      if (typeof f.code === 'string') {
        return f.code === String(addressObject?.official?.postalCode);
      } else {
        return f.code === Number(addressObject?.official?.postalCode);
      }
    }), // Postal code for area
    officialCountry: !_.isEmpty(addressObject?.official?.country)
      ? addressObject?.official?.country
      : // : 'usa', // Country (e.g. can be ISO 3166 2 or 3 letter code)
      anyCountry == 'IN'
      ? 'in'
      : 'usa', // Country (e.g. can be ISO 3166 2 or 3 letter code)
    officialPeriod: {}, // Time period when address was/is in use

    billingAddressObject: addressObject?.billing,
    billingUse: addressObject?.billing?.use, // home | work | temp | old | billing - purpose of this address
    billingType: addressObject?.billing?.type, // postal | physical | both
    billingText: addressObject?.billing?.text, // Text representation of the address
    billingAddressLine1: addressObject?.billing?.line?.[0] ?? '', // Street name, number, direction & P.O. Box etc.
    billingAddressLine2: addressObject?.billing?.line?.[1] ?? '',
    billingCity: addressObject?.billing?.city, // Name of city, town etc.
    billingDistrict: addressObject?.billing?.district, // District name (aka county)
    billingState: !_.isEmpty(addressObject?.billing?.state)
      ? addressObject?.billing?.state
      : // : usaStateListWithCodes?.[0]?.code, // Sub-unit of country (abbreviations ok)
      anyCountry == 'IN'
      ? 'AN'
      : 'AL', // Sub-unit of country (abbreviations ok)
    // billingPostalCode: addressObject?.billing?.postalCode, // Postal code for area
    billingPostalCode: zipCodesSecondary.find(f => {
      if (typeof f.code === 'string') {
        return f.code === String(addressObject?.billing?.postalCode);
      } else {
        return f.code === Number(addressObject?.billing?.postalCode);
      }
    }), // Postal code for area
    billingCountry: !_.isEmpty(addressObject?.billing?.country)
      ? addressObject?.billing?.country
      : // : 'usa', // Country (e.g. can be ISO 3166 2 or 3 letter code)
      anyCountry == 'IN'
      ? 'in'
      : 'usa',
    billingPeriod: {}, // Time period when address was/is in use
  };
};

const FHIRAddress = forwardRef((props, ref) => {
  const selectedVersion = useSelector(
    state => state.changeVersion.selectedVersion
  );
  const { isEdit, addressObject, isCreate, npiData, from } = props;
  const [addressState, setAddressState] = useState(
    addressInitialStateFromProps(addressObject, selectedVersion)
  );

  const reduxDispatch = useDispatch();

  const [stateForProps, setStateForProps] = useState(false);

  const [isStateChanged, setIsStateChanged] = useState(false);

  const [initialState, setInitialState] = useState(false);

  useEffect(() => {
    setInitialState(addressState);
  }, []);

  useEffect(() => {
    const flag = JSON.stringify(initialState) !== JSON.stringify(addressState);
    setIsStateChanged(flag);
    reduxDispatch(setStateDetectorState({ isAddressStateChanged: flag }));
    console.log(
      `IS_STATE_CHANGE_ADDRESS`,
      flag,
      `STATE`,
      initialState,
      addressState
    );
    return () => {
      reduxDispatch(resetStateDetectorState());
    };
  }, [initialState, addressState]);

  const currentReduxPractitionerObject = useSelector(
    state => state?.current?.currentPractitionerObject
  );
  const officialPostalCodeRedux =
    currentReduxPractitionerObject?.address?.official?.postalCode;
  const billingPostalCodeRedux =
    currentReduxPractitionerObject?.address?.billing?.postalCode;

  const [zipcodeOptions, setZipcodeOptions] = useState([]);
  const [zipcodeOptionsSecondary, setZipcodeOptionsSecondary] = useState([]);
  const [officialStates, setOfficialStates] = useState([]);
  const [billingStates, setBillingStates] = useState([]);
  const [count, setCount] = useState(true);

  useEffect(() => {}, [addressState]);

  const addressLine1Ref = useRef(null);
  const addressLine2Ref = useRef(null);
  const cityRef = useRef(null);
  const stateRef = useState(null);
  const countryRef = useRef(null);
  const postalCodeRef = useRef(null);

  const billingAddressLine1Ref = useRef(null);
  const billingAddressLine2Ref = useRef(null);
  const billingCityRef = useRef(null);
  const billingStateRef = useState(null);
  const billingCountryRef = useRef(null);
  const billingPostalCodeRef = useRef(null);

  const addressLine1ValidationObject = {
    name: 'officialAddressLine1',
    value: addressState?.officialAddressLine1,
    label: 'Address Line 1',
    ref: addressLine1Ref,
    width: '98.4%',
    validation: {
      required: true,
      min: 1,
      max: 55,
    },
  };

  const addressLine2ValidationObject = {
    name: 'officialAddressLine2',
    value: addressState?.officialAddressLine2,
    label: 'Address Line 2',
    ref: addressLine2Ref,
    width: '98.4%',
    validation: {
      required: false,
      min: 1,
      max: 55,
    },
  };

  const cityValidationObject = {
    name: 'officialCity',
    value: addressState?.officialCity,
    label: selectedVersion === 'IN' ? 'City/District/Village' : 'City',
    ref: cityRef,
    width: '97%',
    validation: {
      required: true,
      min: 2,
      max: 30,
    },
  };

  let stateValidationObject = {
    name: 'officialState',
    value: addressState?.officialState,
    label: 'State',
    ref: stateRef,
    width: '97%',
    validation: {
      required: true,
    },
  };

  const countryValidation = {
    name: 'officialCountry',
    // value: addressState?.officialCountry,
    value: addressState?.officialCountry?.slice(0, 2)?.toUpperCase(),
    label: 'Country',
    ref: countryRef,
    width: '97%',
    // optionsList: GET_COUNTRIES(),
    optionsList: [
      selectedVersion == 'IN'
        ? { code: 'IN', display: 'India' }
        : // : { code: 'usa', display: 'USA' },
          { code: 'US', display: 'USA' },
    ],
  };

  const postalCodeValidationObject = {
    name: 'officialPostalCode',
    // value: addressState?.officialPostalCode || { code: '', display: '' },
    value:
      addressState?.officialPostalCode != undefined
        ? addressState?.officialPostalCode
        : officialPostalCodeRedux
        ? {
            code: officialPostalCodeRedux,
            display: officialPostalCodeRedux,
          }
        : addressObject?.official?.postalCode
        ? {
            code: addressObject?.official?.postalCode,
            display: addressObject?.official?.postalCode,
          }
        : { code: '', display: '' },
    label: 'Zip Code',
    ref: postalCodeRef,
    width: '97%',
    validation: { required: true },
    zIndex: 1000,
  };

  const billingAddressLine1ValidationObject = {
    name: 'billingAddressLine1',
    value: addressState?.billingAddressLine1,
    label: 'Address Line 1',
    ref: billingAddressLine1Ref,
    width: '98.4%',
    validation: {
      required: true,
      min: 1,
      max: 55,
    },
  };

  const billingAddressLine2ValidationObject = {
    name: 'billingAddressLine2',
    value: addressState?.billingAddressLine2,
    label: 'Address Line 2',
    ref: billingAddressLine2Ref,
    width: '98.4%',
    validation: {
      required: false,
      min: 1,
      max: 55,
    },
  };

  const billingCityValidationObject = {
    name: 'billingCity',
    value: addressState?.billingCity,
    label: selectedVersion === 'IN' ? 'City/District/Village' : 'City',
    ref: billingCityRef,
    width: '97%',
    validation: {
      required: true,
      min: 2,
      max: 30,
    },
  };

  const billingStateValidationObject = {
    name: 'billingState',
    value: addressState?.billingState,
    label: 'State',
    ref: billingStateRef,
    // width: '93%',
    width: '97%',
    // optionsList: usaStateListWithCodes,
    optionsList: billingStates,
    validation: {
      required: true,
    },
  };

  const billingCountryValidation = {
    name: 'billingCountry',
    value: addressState?.billingCountry?.slice(0, 2)?.toUpperCase(),
    label: 'Country',
    ref: billingCountryRef,
    // width: '94%',
    width: '97%',
    // optionsList: GET_COUNTRIES(),
    optionsList: [
      selectedVersion == 'IN'
        ? { code: 'IN', display: 'India' }
        : // : { code: 'usa', display: 'USA' },
          { code: 'US', display: 'USA' },
    ],
  };

  const billingPostalCodeValidationObject = {
    name: 'billingPostalCode',
    // value: addressState?.billingPostalCode || { code: '', display: '' },
    value:
      addressState?.billingPostalCode != undefined
        ? addressState?.billingPostalCode
        : billingPostalCodeRedux
        ? {
            code: billingPostalCodeRedux,
            display: billingPostalCodeRedux,
          }
        : addressObject?.billing?.postalCode
        ? {
            code: addressObject?.billing?.postalCode,
            display: addressObject?.billing?.postalCode,
          }
        : { code: '', display: '' },
    label: 'Zip Code',
    ref: billingPostalCodeRef,
    inputVariant: 'zipcode',
    width: '97%',
    validation: { required: true },
    zIndex: 1000,
  };

  const initialUseState = ['work', 'temp', 'old', 'billing'];
  const initialTypeState = ['postal', 'physical', 'both'];

  useEffect(() => {
    setOfficialStates(
      GET_STATES(addressState?.officialCountry?.slice(0, 2)?.toUpperCase())
    );
  }, [addressState?.officialCountry]);

  useEffect(() => {
    setBillingStates(
      GET_STATES(addressState?.billingCountry?.slice(0, 2)?.toUpperCase())
    );
  }, [addressState?.billingCountry]);

  useEffect(() => {
    setZipcodeOptions([
      ...getZipCodes(
        addressState?.officialState
          ? addressState?.officialState
          : usaStateListWithCodes.length
          ? usaStateListWithCodes[0]?.code
          : '',
        addressState?.officialCountry?.slice(0, 2).toUpperCase()
      ),
    ]);
    setZipcodeOptionsSecondary([
      ...getZipCodes(
        addressState?.billingState
          ? addressState?.billingState
          : usaStateListWithCodes.length
          ? usaStateListWithCodes[0]?.code
          : '',
        addressState?.billingCountry?.slice(0, 2).toUpperCase()
      ),
    ]);
  }, [addressState]);

  useEffect(() => {
    console.log(
      'addressState---',
      addressState,
      '-addressObject-',
      addressObject
    );
  }, [addressState]);

  useEffect(() => {
    if (addressState?.officialCountry != addressObject?.official?.country) {
      if (
        addressState?.officialCountry != 'usa' &&
        addressState?.officialCountry != 'in'
      ) {
        setAddressState({
          ...addressState,
          officialState: undefined,
          officialPostalCode: '',
        });
      }
    } else {
      if (addressState?.officialCountry == 'US') {
        if (addressState?.officialPostalCode?.display?.length > 5) {
          setAddressState({
            ...addressState,
            officialState: undefined,
            officialPostalCode: '',
          });
        } else if (addressState?.officialPostalCode?.display == '') {
          setAddressState({
            ...addressState,
            officialState: undefined,
            officialPostalCode: '',
          });
        }
      } else if (addressState?.officialCountry == 'IN') {
        if (addressState?.officialPostalCode?.display?.length < 6) {
          setAddressState({
            ...addressState,
            officialState: undefined,
            officialPostalCode: '',
          });
        } else if (addressState?.officialPostalCode?.display == '') {
          setAddressState({
            ...addressState,
            officialState: undefined,
            officialPostalCode: '',
          });
        }
      }
    }
  }, [addressState?.officialCountry]);

  useEffect(() => {
    if (!addressState?.isBillingAndPrimarySame) {
      if (addressState?.billingCountry != addressObject?.billing?.country) {
        setAddressState({
          ...addressState,
          billingState: undefined,
          billingPostalCode: '',
        });
      } else {
        if (addressState?.billingCountry == 'US') {
          if (addressState?.billingPostalCode?.display?.length > 5) {
            setAddressState({
              ...addressState,
              billingState: undefined,
              billingPostalCode: '',
            });
          } else if (addressState?.billingPostalCode?.display == '') {
            setAddressState({
              ...addressState,
              billingState: undefined,
              billingPostalCode: '',
            });
          }
        } else if (addressState?.billingCountry == 'IN') {
          if (addressState?.billingPostalCode?.display?.length < 7) {
            setAddressState({
              ...addressState,
              billingState: undefined,
              billingPostalCode: '',
            });
          } else if (addressState?.billingPostalCode?.display == '') {
            setAddressState({
              ...addressState,
              billingState: undefined,
              billingPostalCode: '',
            });
          }
        }
      }
    }
  }, [addressState?.billingCountry]);

  useEffect(() => {
    if (npiData && Object.keys(npiData.data).length > 0) {
      let npi = npiData?.data;
      let officialNpiData = npiData?.data?.addresses?.filter(item => {
        if (item?.address_purpose === 'LOCATION') {
          return item;
        }
      });

      let billingNpiData = npiData?.data?.addresses?.filter(item => {
        if (item?.address_purpose === 'MAILING') {
          return item;
        }
      });

      if (
        (npi &&
          npi?.addresses?.length > 1 &&
          npi?.addresses[0]?.address_1 !== npi?.addresses[1]?.address_1) ||
        npi?.addresses[0]?.city !== npi?.addresses[1]?.city ||
        npi?.addresses[0]?.state !== npi?.addresses[1]?.state ||
        npi?.addresses[0]?.country_code !== npi?.addresses[1]?.country_code ||
        npi?.addresses[0]?.postal_code !== npi?.addresses[1]?.postal_code
      ) {
        setIsSameAsPrimary(false);
        setAddressState({
          ...addressState,
          isBillingAndPrimarySame: false,
          officialAddressLine1: officialNpiData[0]?.address_1,
          officialCity: officialNpiData[0]?.city,
          officialState: officialNpiData[0]?.state,
          officialPostalCode: {
            code: officialNpiData[0]?.postal_code?.slice(0, 5),
            display: officialNpiData[0]?.postal_code?.slice(0, 5),
          },
          billingAddressLine1: billingNpiData[0]?.address_1,
          billingCity: billingNpiData[0]?.city,
          billingState: billingNpiData[0]?.state,
          billingPostalCode: {
            code: billingNpiData[0]?.postal_code?.slice(0, 5),
            display: billingNpiData[0]?.postal_code?.slice(0, 5),
          },
        });
      } else {
        setIsSameAsPrimary(true);
        setAddressState({
          ...addressState,
          isBillingAndPrimarySame: true,
          officialAddressLine1: officialNpiData[0]?.address_1,
          officialCity: officialNpiData[0]?.city,
          officialState: officialNpiData[0]?.state,
          officialPostalCode: {
            code: officialNpiData[0]?.postal_code.slice(0, 5),
            display: officialNpiData[0]?.postal_code.slice(0, 5),
          },
        });
      }
    }
  }, [npiData]);

  // useEffect(() => {
  //   if (npiData && Object.keys(npiData.data).length > 0) {
  //     //this will check if npiData exists and has some properties to work with
  //     let npi = npiData.data;
  //     let postalCode = npi?.addresses[1]?.postal_code.slice(0, 5);
  //     if (npi.addresses.length > 1) {
  //       setAddressState({
  //         ...addressState,
  //         officialAddressLine1: npi?.addresses[1].address_1,
  //         officialCity: npi?.addresses[1]?.city,
  //         officialState: npi?.addresses[1]?.state,
  //         officialPostalCode: { code: postalCode, display: postalCode },
  //       });
  //     }
  //     if (
  //       npi &&
  //       npi.addresses.length > 1 &&
  //       npi.addresses[0].address_1 !== npi.addresses[1].address_1
  //     ) {
  //       //checks if the mailing address and the primary address are same or not
  //       setIsSameAsPrimary(false);
  //       setAddressState({
  //         ...addressState,
  //         isBillingAndPrimarySame: false,
  //       });
  //       setCount(false);
  //     } else {
  //       setIsSameAsPrimary(true);
  //       setAddressState({
  //         ...addressState,
  //         isBillingAndPrimarySame: true,
  //       });
  //       setCount(true);
  //     }
  //   }
  // }, [npiData]);

  const handleOnChange = event => {
    const { name, value } = event?.target;
    let tempAddressState = structuredClone(addressState);
    if (name === 'officialState') {
      tempAddressState.officialPostalCode = { code: '', display: '' };
      let zipCodes = getZipCodes(value);
      setZipcodeOptions([...zipCodes]);
    } else if (name === 'billingState') {
      tempAddressState.billingPostalCode = { code: '', display: '' };
      let zipCodes = getZipCodes(value);
      setZipcodeOptionsSecondary([...zipCodes]);
    }
    let newValue = value;
    const fieldsToCapitalize = [
      'officialAddressLine1',
      'officialAddressLine2',
      'officialCity',
      'billingAddressLine1',
      'billingAddressLine2',
      'billingCity',
    ];

    if (fieldsToCapitalize.includes(name)) {
      newValue = capitalizeFirstLetter(newValue);
    }

    setAddressState({
      ...tempAddressState,
      [name]: newValue,
    });
  };

  const [isSameAsPrimary, setIsSameAsPrimary] = useState(false);

  // useEffect(() => {
  //   if (!count) {
  //     if (npiData && Object.keys(npiData.data).length > 0) {
  //       let npi = npiData.data;
  //       let postalCode = npi?.addresses[0]?.postal_code.slice(0, 5);
  //       setAddressState({
  //         ...addressState,
  //         billingAddressLine1: npi?.addresses[0].address_1,
  //         billingCity: npi?.addresses[0]?.city,
  //         billingState: npi?.addresses[0]?.state,
  //         billingPostalCode: { code: postalCode, display: postalCode },
  //       });
  //     }
  //   } else if (npiData && Object.keys(npiData.data).length > 0) {
  //     setAddressState({
  //       ...addressState,
  //       billingAddressLine1: '',
  //       billingCity: '',
  //       billingState: '',
  //       billingPostalCode: { code: '', display: '' },
  //     });
  //   }
  // }, [count]);

  useImperativeHandle(ref, () => ({
    handleOnUpdate() {
      const {
        officialUse,
        officialType,
        officialText,
        officialAddressLine1,
        officialAddressLine2,
        officialCity,
        officialState,
        officialCountry,
        officialPostalCode,
        billingUse,
        billingType,
        billingText,
        billingAddressLine1,
        billingAddressLine2,
        billingCity,
        billingState,
        billingCountry,
        billingPostalCode,
      } = addressState;

      let addressList = [];

      let officialParam = {};
      let billingParam = {};

      officialParam.use = 'home';
      officialParam.type = 'postal';
      billingParam.use = 'work';
      billingParam.type = 'postal';
      // officialParam.use = 'work'
      // officialParam.type = 'postal'
      // billingParam.use = 'work'
      // billingParam.type = 'postal'
      // officialParam.text = text;
      officialParam.line = [
        officialAddressLine1?.trim(),
        officialAddressLine2?.trim(),
      ];
      officialParam.city = officialCity?.trim();
      officialParam.state = officialState;
      officialParam.country = officialCountry;
      // officialParam.postalCode = String(officialPostalCode?.code);
      officialParam.postalCode =
        officialPostalCode?.code !== undefined
          ? String(officialPostalCode?.code)
          : officialPostalCodeRedux
          ? officialPostalCodeRedux
          : addressObject?.official?.postalCode;
      // officialParam.postalCode = officialPostalCode;

      if (addressState?.isBillingAndPrimarySame) {
        billingParam.line = [
          officialAddressLine1?.trim(),
          officialAddressLine2?.trim(),
        ];
        billingParam.city = officialCity?.trim();
        billingParam.state = officialState;
        billingParam.country = officialCountry;
        billingParam.postalCode = String(officialPostalCode?.code);
      } else {
        billingParam.line = [
          billingAddressLine1?.trim(),
          billingAddressLine2?.trim(),
        ];
        billingParam.city = billingCity?.trim();
        billingParam.state = billingState;
        billingParam.country = billingCountry;
        // billingParam.postalCode = String(billingPostalCode?.code);
        billingParam.postalCode =
          billingPostalCode?.code != undefined
            ? String(billingPostalCode?.code)
            : billingPostalCodeRedux
            ? billingPostalCodeRedux
            : addressObject?.billing?.postalCode;
      }

      console.log('billingPostalCodeRedux---', billingPostalCodeRedux);

      addressList.push(officialParam);
      // let postalForValidationAddressList = addressList[0]["postalCode"]
      // if (postalForValidationAddressList) {
      //   addressList[0]["postalCode"] = String(postalForValidationAddressList?.code)
      // }
      if (!addressState?.isBillingAndPrimarySame) {
        addressList.push(billingParam);
      }

      const validationData = [];
      validationData.push(addressLine1Ref?.current?.onValidate());
      validationData.push(addressLine2Ref?.current?.onValidate());
      validationData.push(cityRef?.current?.onValidate());
      validationData.push(stateRef?.current?.onValidate());
      validationData.push(countryRef?.current?.onValidate());
      validationData.push(postalCodeRef?.current?.onValidate());
      let postalForValidation = validationData.findIndex(
        f => f.name === 'officialPostalCode'
      );
      if (postalForValidation) {
        validationData[postalForValidation] = {
          name: 'officialPostalCode',
          // valid: Boolean(validationData[postalForValidation]?.value?.code),
          valid: Boolean(
            // .length == 5
            validationData[postalForValidation]?.value?.code?.toString()
              .length >= 5
          ),
          value: validationData[postalForValidation]?.value?.code,
        };
      }

      if (!addressState?.isBillingAndPrimarySame) {
        validationData.push(billingAddressLine1Ref?.current?.onValidate());
        validationData.push(billingAddressLine2Ref?.current?.onValidate());
        validationData.push(billingCityRef?.current?.onValidate());
        validationData.push(billingStateRef?.current?.onValidate());
        validationData.push(billingCountryRef?.current?.onValidate());
        validationData.push(billingPostalCodeRef?.current?.onValidate());
        let postalForValidation = validationData.findIndex(
          f => f.name === 'billingPostalCode'
        );
        if (postalForValidation) {
          validationData[postalForValidation] = {
            name: 'billingPostalCode',
            // valid: Boolean(validationData[postalForValidation]?.value?.code),
            valid: Boolean(
              // .length == 5
              validationData[postalForValidation]?.value?.code?.toString()
                .length >= 5
            ),
            value: validationData[postalForValidation]?.value?.code,
          };
        }
      }

      let valid = true;
      for (let item of validationData) {
        if (!item?.valid) {
          valid = false;
          break;
        }
      }

      addressList.valid = valid;
      addressList.isStateChanged = isStateChanged;

      return addressList;
    },
  }));

  useEffect(() => {
    //setAddressState(addressInitialStateFromProps(addressObject));
    if (
      _.isUndefined(addressState?.billingAddressLine1) &&
      _.isUndefined(addressState?.billingAddressLine2)
    ) {
      setIsSameAsPrimary(true);
      setAddressState({
        ...addressState,
        isBillingAndPrimarySame: true,
      });
    }
  }, [props?.addressObject]);

  return (
    <Grid container spacing={2}>
      <Grid xs={12} sx={{ border: '0px solid black' }} container item>
        <Grid item sx={12}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {props?.customTitle ? (
              props?.customTitle('Primary Address')
            ) : (
              <>
                <AddCircle />
                <Typography>Primary Address</Typography>
              </>
            )}
          </Box>
        </Grid>
      </Grid>

      <Grid item container xs={12}>
        {isEdit ? (
          <TextFieldWithTopLabelComponent
            onChange={handleOnChange}
            {...addressLine1ValidationObject}
          />
        ) : (
          // textFieldWithTopLabel({
          //     name: "officialAddressLine1",
          //     label: "Address Line 1",
          //     width: "98.4%",
          //     value: addressState?.officialAddressLine1,
          //     onChange: handleOnChange,
          //   })
          paperWithTopLabelAndBottomValue({
            label: 'Address Line 1',
            value: addressState?.officialAddressLine1,
          })
        )}
      </Grid>
      <Grid item container xs={12}>
        {isEdit ? (
          <TextFieldWithTopLabelComponent
            onChange={handleOnChange}
            {...addressLine2ValidationObject}
          />
        ) : (
          // textFieldWithTopLabel({
          //     name: "officialAddressLine2",
          //     label: "Address Line 2",
          //     width: "98.4%",
          //     value: addressState?.officialAddressLine2,
          //     onChange: handleOnChange,
          //   })
          paperWithTopLabelAndBottomValue({
            label: 'Address Line 2',
            value: addressState?.officialAddressLine2,
          })
        )}
      </Grid>

      <Grid item container xs={12} spacing={1}>
        <Grid item sm={6}>
          {isEdit ? (
            <SelectWithTopLabelComponent
              onChange={handleOnChange}
              {...countryValidation}
            />
          ) : (
            paperWithTopLabelAndBottomValue({
              label: 'Country',
              value:
                usaStateListWithCodes?.[
                  _.findIndex(
                    usaStateListWithCodes,
                    o => o?.code === addressState?.officialState
                  )
                ]?.country,
            })
          )}
        </Grid>
        <Grid item sm={6}>
          {isEdit ? (
            <SelectWithTopLabelComponent
              onChange={handleOnChange}
              {...stateValidationObject}
              optionsList={officialStates}
            />
          ) : (
            paperWithTopLabelAndBottomValue({
              label: 'State',
              value:
                usaStateListWithCodes?.[
                  _.findIndex(
                    usaStateListWithCodes,
                    o => o?.code === addressState?.officialState
                  )
                ]?.display,
            })
          )}
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={1}>
        <Grid item sm={6}>
          {isEdit ? (
            <TextFieldWithTopLabelComponent
              onChange={handleOnChange}
              {...cityValidationObject}
            />
          ) : (
            paperWithTopLabelAndBottomValue({
              label:
                selectedVersion === 'IN' ? 'City/District/Village' : 'City',
              value: addressState?.officialCity,
            })
          )}
        </Grid>
        <Grid item sm={6}>
          {isEdit ? (
            <AutoCompleteWithTopLabel
              from={'practitionerCreate'}
              onChange={handleOnChange}
              optionsList={zipcodeOptions}
              disablePortal={true}
              disableClearable
              {...postalCodeValidationObject}
              addressState={addressState}
              stateForProps={stateForProps}
              setStateForProps={setStateForProps}
            />
          ) : (
            paperWithTopLabelAndBottomValue({
              label: 'Zip code',
              value: addressState?.officialPostalCode?.code,
            })
          )}
        </Grid>
      </Grid>

      <Grid xs={12} sx={{ border: '0px solid black' }} container item>
        <Grid item sx={12}>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexDirection: 'column',
            }}
          >
            {
              !addressState?.isBillingAndPrimarySame && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {props?.customTitle ? (
                      props?.customTitle('Billing Address')
                    ) : (
                      <>
                        <AddCircle />
                        <Typography>Billing Address</Typography>
                      </>
                    )}
                  </Box>
                </Grid>
              )
              // <Typography>Billing Address</Typography>
            }
          </Box>
        </Grid>
      </Grid>

      {isEdit && (
        <Grid xs={12} sx={{ border: '0px solid black' }} container item>
          {addressState?.isBillingAndPrimarySame && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {props?.customTitle ? (
                  props?.customTitle('Billing Address')
                ) : (
                  <>
                    <AddCircle />
                    <Typography>Billing Address</Typography>
                  </>
                )}
              </Box>
            </Grid>
          )}
          <Box sx={{ display: 'flex', gap: 0, alignItems: 'center' }}>
            <Checkbox
              checked={addressState?.isBillingAndPrimarySame}
              name="isSameAsPrimary"
              onChange={() => {
                setIsSameAsPrimary(!isSameAsPrimary);
                setAddressState({
                  ...addressState,
                  isBillingAndPrimarySame:
                    !addressState?.isBillingAndPrimarySame,
                });
              }}
            />
            <Typography>Billing address is same as Primary address</Typography>
          </Box>
        </Grid>
      )}

      {!addressState?.isBillingAndPrimarySame && (
        <>
          <Grid item container xs={12}>
            {isEdit ? (
              <TextFieldWithTopLabelComponent
                onChange={handleOnChange}
                {...billingAddressLine1ValidationObject}
              />
            ) : (
              paperWithTopLabelAndBottomValue({
                label: 'Address Line 1',
                value: addressState?.billingAddressLine1,
              })
            )}
          </Grid>
          <Grid item container xs={12}>
            {isEdit ? (
              <TextFieldWithTopLabelComponent
                onChange={handleOnChange}
                {...billingAddressLine2ValidationObject}
              />
            ) : (
              paperWithTopLabelAndBottomValue({
                label: 'Address Line 2',
                value: addressState?.billingAddressLine2,
              })
            )}
          </Grid>

          <Grid item container xs={12} spacing={1}>
            {/* <Grid item sm={6}>
              {isEdit ? (
                <SelectWithTopLabelComponent
                  onChange={handleOnChange}
                  {...billingStateValidationObject}
                  // optionsList={billingStates}
                />
              ) : (
                paperWithTopLabelAndBottomValue({
                  label: 'State',
                  value:
                    usaStateListWithCodes?.[
                      _.findIndex(
                        usaStateListWithCodes,
                        o => o?.code === addressState?.billingState
                      )
                    ]?.display,
                })
              )}
            </Grid> */}
            {/* cc */}
            <Grid item sm={6}>
              {isEdit ? (
                <SelectWithTopLabelComponent
                  onChange={handleOnChange}
                  {...billingCountryValidation}
                />
              ) : (
                paperWithTopLabelAndBottomValue({
                  label: 'Country',
                  value:
                    usaStateListWithCodes?.[
                      _.findIndex(
                        usaStateListWithCodes,
                        o => o?.code === addressState?.billingState
                      )
                    ]?.country,
                })
              )}
            </Grid>
            <Grid item sm={6}>
              {isEdit ? (
                <SelectWithTopLabelComponent
                  onChange={handleOnChange}
                  {...billingStateValidationObject}
                  // optionsList={billingStates}
                />
              ) : (
                paperWithTopLabelAndBottomValue({
                  label: 'State',
                  value:
                    usaStateListWithCodes?.[
                      _.findIndex(
                        usaStateListWithCodes,
                        o => o?.code === addressState?.billingState
                      )
                    ]?.display,
                })
              )}
            </Grid>
            {/* cc */}
          </Grid>
          <Grid item container xs={12} spacing={1}>
            {/* <Grid item sm={6}>
              {isEdit ? (
                <SelectWithTopLabelComponent
                  onChange={handleOnChange}
                  {...billingCountryValidation}
                />
              ) : (
                paperWithTopLabelAndBottomValue({
                  label: 'Country',
                  value:
                    usaStateListWithCodes?.[
                      _.findIndex(
                        usaStateListWithCodes,
                        o => o?.code === addressState?.billingState
                      )
                    ]?.country,
                })
              )}
            </Grid> */}
            {/* cc */}

            {/* cc */}
            <Grid item sm={6}>
              {isEdit ? (
                <TextFieldWithTopLabelComponent
                  onChange={handleOnChange}
                  {...billingCityValidationObject}
                />
              ) : (
                paperWithTopLabelAndBottomValue({
                  label:
                    selectedVersion === 'IN' ? 'City/District/Village' : 'City',
                  value: addressState?.billingCity,
                })
              )}
            </Grid>
            <Grid item sm={6}>
              {isEdit ? (
                <AutoCompleteWithTopLabel
                  // from={from}
                  from={'practitionerCreate'}
                  onChange={handleOnChange}
                  disablePortal={true}
                  disableClearable
                  optionsList={zipcodeOptionsSecondary}
                  {...billingPostalCodeValidationObject}
                  addressState={addressState}
                  stateForProps={stateForProps}
                  setStateForProps={setStateForProps}
                />
              ) : (
                paperWithTopLabelAndBottomValue({
                  label: 'Zip code',
                  value: addressState?.billingPostalCode?.code,
                })
              )}
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
  );
});

export default FHIRAddress;
